import { values } from "lodash";
import { ThemeId, Theme, DeviceType, DeviceFamily, DeviceTypeId } from 'shared/models';
import { matchesResolution } from 'shared/util/matchesResolution';

//
// When showing themes eligible for a particular device, we make sure that the
// theme is the correct dimensions (or at least aspect ratio) and is suitable
// for the given device type. And, for example, a PI (position indicator) device
// must have a PI theme. Similarly, a non-PI device may only have a non-PI theme.
//
export function filterEligibleThemes(themes: Record<ThemeId, Theme> | null, deviceType: DeviceType | null, isPI: boolean, deviceTypes: Record<DeviceTypeId, DeviceType>) {
    let eligibleThemes =
        values(themes || {})
            .filter(theme => theme.published)
            .filter(filterThemeByPI(isPI));

    if (deviceType) {
        const { deviceCapabilities, deviceFamily, pixelHeight, pixelWidth } = deviceType;
        eligibleThemes =
            eligibleThemes
                .filter(filterThemeByDeviceCapabilities(deviceCapabilities, deviceFamily!, deviceTypes))
                .filter(filterThemeByResolution(deviceCapabilities, pixelWidth, pixelHeight));
    }

    return eligibleThemes;
}

//
// A Matisse theme can only appear on a Matisse device
// A TTG theme can only appear on a TTG device
// A generic theme can appear on any device (albeit, with other restrictions)
//
function filterThemeByDeviceCapabilities(capabilities: DeviceType['deviceCapabilities'], deviceFamily: DeviceFamily, deviceTypes: Record<DeviceTypeId, DeviceType>) {
    return ({ requirements }: Theme): boolean => {
        const themeDeviceFamily = requirements?.deviceTypeId ? deviceTypes[requirements.deviceTypeId].deviceFamily : null;

        return (
            (requirements.TTG && capabilities.TTG) ||
            (requirements.Matisse && capabilities.Matisse) ||
            (!requirements.TTG && !requirements.Matisse && themeDeviceFamily !== DeviceFamily.Giotto) ||
            (themeDeviceFamily === deviceFamily)
        );
    };
}

//
// A PI theme can only appear on a PI device
// A non-PI theme can only appear on a non-PI device
//
function filterThemeByPI(isPI: boolean) {
    return ({ requirements }: Theme): boolean => (
        !!requirements.pi === isPI
    );
}

//
// A theme can only be scheduled to a device that matches in resolution
//
function filterThemeByResolution(capabilities: DeviceType['deviceCapabilities'], deviceWidth: number | null, deviceHeight: number | null) {
    return ({ width: themeWidth, height: themeHeight }: Theme): boolean => (
        !!capabilities.anySize || // Allow any size to appear on a generic device
        matchesResolution(deviceWidth || 0, deviceHeight || 0, themeWidth, themeHeight) ||
        // Allow for odd Matisse resizing quirk (can't put a 1280x720 on the Matisse but
        // can put a 1920x1080 and it will resize down to 1280x720 correctly)
        (!!capabilities.Matisse && matchesResolution(deviceWidth || 0, deviceHeight || 0, 1280, 720) && matchesResolution(themeWidth, themeHeight, 1920, 1080))
    );
}
